import React, { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  IconButton,
  Typography,
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { describeFailure, Failure } from '../api/failureReason'
import FailureMessage from './FailureMessage'
import { formatDays, formatDayTimeIn, relativeFrom } from '../format'
import { advancedColors } from '../theme'
import AnswerList from './AnswerList'
import DialogHeader from './DialogHeader'
import {
  ScoredInstance,
  changeColor,
  completionTimeLabel,
  resolvedInstanceId,
  vsPreviousLabel,
  vsPreviousPercent,
} from './questionnaireHistory'
import SurveyCompletionHistory from './SurveyCompletionHistory'

type Props = {
  label: string
  onClose: () => void
  completed: boolean
  sentAt: string | null
  completedAt: string | null
  timezone: string | null | undefined
  instances: ScoredInstance[]
  scheduledFor?: string | null
  repeatCadenceInDays?: number | null
  onUnassign?: () => Promise<void>
  onAssignAgain?: () => Promise<void>
}

const SurveyDetail = ({
  label,
  onClose,
  completed,
  sentAt,
  completedAt,
  instances,
  timezone,
  scheduledFor,
  repeatCadenceInDays,
  onUnassign,
  onAssignAgain,
}: Props) => {
  const [instanceId, setInstanceId] = useState('')
  const [tableExpanded, setTableExpanded] = useState(false)
  const [confirmingUnassign, setConfirmingUnassign] = useState(false)
  const [unassigning, setUnassigning] = useState(false)
  const [assigningAgain, setAssigningAgain] = useState(false)
  const [unassignError, setUnassignError] = useState<Failure | null>(null)
  const [assignError, setAssignError] = useState<Failure | null>(null)

  const selectedId = resolvedInstanceId(instanceId, instances)
  const index = instances.findIndex((instance) => instance.id === selectedId)
  const instance = instances[index]
  const previous = instances[index + 1]
  const changePercent = vsPreviousPercent(instance?.score ?? null, previous?.score ?? null)
  const sentLabel = relativeFrom(sentAt)
  const completedLabel = relativeFrom(completedAt)
  const unassignCopy = repeatCadenceInDays
    ? 'Unassigning cancels this survey and stops all future repeats. Scored history is kept.'
    : "It hasn't been completed yet — unassigning cancels the request. This can't be undone."

  const dismissUnassign = () => {
    if (unassigning) {
      return
    }
    setConfirmingUnassign(false)
    setUnassignError(null)
  }

  const confirmUnassign = async () => {
    if (!onUnassign || completed || unassigning) {
      return
    }
    setUnassigning(true)
    setUnassignError(null)
    try {
      await onUnassign()
    } catch (error) {
      setUnassignError(describeFailure('Failed to unassign this survey.', error))
      setUnassigning(false)
    }
  }

  const assignAgain = async () => {
    if (!onAssignAgain || assigningAgain) {
      return
    }
    setAssigningAgain(true)
    setAssignError(null)
    try {
      await onAssignAgain()
    } catch (error) {
      setAssignError(describeFailure('Failed to assign this survey.', error))
      setAssigningAgain(false)
    }
  }

  return (
    <>
      <DialogHeader
        title={label}
        onClose={onClose}
        action={
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1, maxWidth: '55vw' }}>
            {!completed && onUnassign && (
              <Button size="small" color="error" variant="contained" onClick={() => setConfirmingUnassign(true)}>
                Unassign
              </Button>
            )}
            {onAssignAgain && (
              <Button size="small" variant="outlined" disabled={assigningAgain} onClick={assignAgain}>
                {assigningAgain ? 'Assigning…' : 'Assign again'}
              </Button>
            )}
          </Box>
        }
      />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
        {!completed && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, flexShrink: 0 }}>
            <Box>
              <Typography variant="caption" sx={{ color: advancedColors.inkMuted }}>
                Next survey
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {scheduledFor ? formatDayTimeIn(scheduledFor, timezone) ?? 'Time unavailable' : 'Available now'}
              </Typography>
              {!scheduledFor && sentLabel && (
                <Typography variant="caption" sx={{ color: advancedColors.inkMuted }}>
                  Assigned {sentLabel}
                </Typography>
              )}
            </Box>
            {!!repeatCadenceInDays && (
              <Box>
                <Typography variant="caption" sx={{ color: advancedColors.inkMuted }}>
                  Repeats
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Every {formatDays(repeatCadenceInDays)} from assignment
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {completed && instances.length > 0 && (
          <Typography variant="body2" sx={{ color: advancedColors.inkMuted }}>
            No upcoming survey.
          </Typography>
        )}

        {completed && instances.length === 0 && (
          <Typography variant="body2" sx={{ color: advancedColors.inkMuted }}>
            Completed{completedLabel ? ` ${completedLabel}` : ''}
            {' — no scored history for this instrument yet.'}
          </Typography>
        )}

        {instances.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              flex: '1 1 auto',
            }}
          >
            <Divider sx={{ mb: 2 }} />
            {instances.length > 1 && (
              <SurveyCompletionHistory
                instances={instances}
                timezone={timezone}
                selectedId={selectedId}
                onSelect={setInstanceId}
                onTableExpandedChange={setTableExpanded}
              />
            )}
            {instances.length === 1 && (
              <Button
                size="small"
                variant="outlined"
                aria-pressed={selectedId === instances[0].id}
                onClick={() => setInstanceId(instances[0].id)}
                sx={{ justifyContent: 'flex-start', flexShrink: 0 }}
              >
                {completionTimeLabel(instances[0], timezone)} · Score {instances[0].score ?? '—'}
              </Button>
            )}

            {instance && (
              <Box
                role="region"
                aria-label="Selected completion details"
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: advancedColors.cardBg,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  flexShrink: tableExpanded ? 0 : 1,
                  maxHeight: tableExpanded ? 'min(280px, 36dvh)' : 'none',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexShrink: 0,
                    mb: 1,
                  }}
                >
                  <Typography
                    component="div"
                    variant="caption"
                    sx={{ color: advancedColors.inkMuted, whiteSpace: 'nowrap', overflowX: 'auto', minWidth: 0 }}
                  >
                    <span>{completionTimeLabel(instance, timezone)}</span>
                    {' · '}
                    <span>Score {instance.score ?? '—'}</span>
                    {previous && (
                      <>
                        {' · '}
                        <Box component="span" sx={{ color: changeColor(changePercent) }}>
                          {vsPreviousLabel(changePercent)}
                        </Box>
                      </>
                    )}
                  </Typography>
                  <IconButton size="small" aria-label="Close completion details" onClick={() => setInstanceId('')}>
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box
                  role="region"
                  aria-label="Completion answers"
                  tabIndex={0}
                  sx={{ minHeight: 0, overflowY: 'auto' }}
                >
                  {instance.answers.length > 0 ? (
                    <AnswerList answers={instance.answers} />
                  ) : (
                    <Typography variant="body2" sx={{ color: advancedColors.inkMuted }}>
                      No individual answers available for this completion.
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {assignError && (
          <Typography variant="body2" color="error">
            <FailureMessage failure={assignError} />
          </Typography>
        )}
      </DialogContent>

      <Dialog open={!completed && !!onUnassign && confirmingUnassign} onClose={dismissUnassign} maxWidth="xs" fullWidth>
        <DialogHeader title="Unassign this survey?" onClose={dismissUnassign} />
        <DialogContent>
          <DialogContentText>{unassignCopy}</DialogContentText>
          {unassignError && (
            <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
              <FailureMessage failure={unassignError} />
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={dismissUnassign} disabled={unassigning}>
            Cancel
          </Button>
          <Button color="error" variant="contained" disabled={unassigning} onClick={confirmUnassign}>
            {unassigning ? 'Unassigning…' : 'Unassign'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default SurveyDetail
